import React, { type ReactNode } from 'react';
import { ActionButton, type ActionButtonData } from '../action-button/ActionButton';
import {
  HorizontalScrollTabs,
  type HorizontalScrollTabsData,
} from '../horizontal-scroll-tabs/HorizontalScrollTabs';
import { t } from '../../services/locale';
import { ProfileBookingsList, type ProfileBookingsListData } from './ProfileBookingsList';
import { ProfileDocumentsList, type ProfileDocumentsListData } from './ProfileDocumentsList';
import { ProfileEditForm, type ProfileEditFormData } from './ProfileEditForm';
import { ProfileLinkedAccounts, type ProfileLinkedAccountsData } from './ProfileLinkedAccounts';
import { ProfileMembershipList, type ProfileMembershipListData } from './ProfileMembershipList';
import { ProfilePaymentMethods, type ProfilePaymentMethodsData } from './ProfilePaymentMethods';
import { ProfileSummary, type ProfileSummaryData } from './ProfileSummary';
import { ProfileTransactionsList, type ProfileTransactionsListData } from './ProfileTransactionsList';

export type ProfilePageTab = 'my-bookings' | 'my-membership' | 'my-transactions' | 'linked-accounts' | string;

export type ProfilePageData = {
  /** When false, the page renders nothing so it can ship inert. Default true. */
  profilePageEnabled?: boolean;
  editProfile?: boolean;
  firstName?: string | null;
  lastName?: string | null;
  summary?: ProfileSummaryData;
  editForm?: ProfileEditFormData;
  documents?: ProfileDocumentsListData;
  paymentMethods?: ProfilePaymentMethodsData;
  changePasswordActionData?: ActionButtonData;
  tabs?: HorizontalScrollTabsData;
  activeTab?: ProfilePageTab;
  bookings?: ProfileBookingsListData;
  membership?: ProfileMembershipListData;
  transactions?: ProfileTransactionsListData;
  linkedAccounts?: ProfileLinkedAccountsData;
  onChangePassword?: () => void;
  /** Optional chrome callback — not required to render content. */
  onClose?: () => void;
  /** Optional chrome callback — not required to render content. */
  onLogOut?: () => void;
  /**
   * Optional slot for modal chrome (mobile X). ProfilePage does not own dismiss
   * behaviour — ProfileDialog supplies this node.
   */
  mobileCloseSlot?: ReactNode;
  /** Optional slot for modal chrome (desktop close ActionButton). */
  desktopCloseSlot?: ReactNode;
};

type Props = Readonly<{
  data?: ProfilePageData;
}>;

function LeftColumn({ data }: Readonly<{ data: ProfilePageData }>) {
  return (
    <div className="modal-body-left">
      {data.mobileCloseSlot}
      {data.editProfile ? (
        <ProfileEditForm data={data.editForm} />
      ) : (
        <div className="modal-body-left-notediting">
          <ProfileSummary data={data.summary} />
          <div className="list-label">
            <span>{t('WEB_DOCUMENTS')}</span>
          </div>
          <ProfileDocumentsList data={data.documents} />
          <ProfilePaymentMethods data={data.paymentMethods} />
          {data.changePasswordActionData ? (
            <span className="profile-action-button">
              <ActionButton
                data={{
                  ...data.changePasswordActionData,
                  onClick: data.onChangePassword,
                }}
              />
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}

function RightColumn({ data }: Readonly<{ data: ProfilePageData }>) {
  const tab = data.activeTab ?? data.tabs?.activeValue ?? 'my-bookings';

  return (
    <div className="modal-body-right">
      <div className="top">
        {[data.firstName, data.lastName].filter(Boolean).join(' ')}
        {data.desktopCloseSlot}
      </div>
      <div className="tabs-wrapper">
        {data.tabs ? (
          <HorizontalScrollTabs data={data.tabs} />
        ) : null}
      </div>
      {tab === 'my-bookings' ? (
        <div className="tab tab-bookings">
          <ProfileBookingsList data={data.bookings} />
        </div>
      ) : null}
      {tab === 'my-membership' ? (
        <div className="tab tab-membership">
          <ProfileMembershipList data={data.membership} />
        </div>
      ) : null}
      {tab === 'my-transactions' ? (
        <div className="tab tab-transactions">
          <ProfileTransactionsList data={data.transactions} />
        </div>
      ) : null}
      {tab === 'linked-accounts' ? (
        <div className="tab tab-linked-accounts">
          <ProfileLinkedAccounts data={data.linkedAccounts} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Unwired profile layout (RNA-5275): left + right columns, no Bootstrap modal
 * chrome, overlay, or dismiss controls. Close/log-out are optional props only.
 */
export function ProfilePage({ data }: Props) {
  if (!data || data.profilePageEnabled === false) {
    return null;
  }

  return (
    <div className="modal-body" data-testid="profile-page">
      <LeftColumn data={data} />
      <RightColumn data={data} />
    </div>
  );
}
